import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";
import { link } from "ionicons/icons";

// Define question types
type QuestionType = "picture" | "identifyOddOneOut" | "trueFalse" | "fillInTheBlanks" | "multipleChoice";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface IdentifyOddOneOutQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface TrueFalseQuestion {
    question: string;
    correct: boolean;
}

interface FillInTheBlanksQuestion {
    question: string;
    correct: string;
    options: string[];
}

const AnimalQuiz: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<
        PictureQuestion | IdentifyOddOneOutQuestion | MultipleChoiceQuestion | TrueFalseQuestion | FillInTheBlanksQuestion | null
    >(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | IdentifyOddOneOutQuestion | MultipleChoiceQuestion | TrueFalseQuestion | FillInTheBlanksQuestion)[]>>({
        picture: [],
        identifyOddOneOut: [],
        multipleChoice: [],
        trueFalse: [],
        fillInTheBlanks: []
    });

    const fetchQuestions = async () => {
        try {
            let multipleChoiceQuestion: MultipleChoiceQuestion[] = [];
            let identifyOddOneOutQuestion: IdentifyOddOneOutQuestion[] = [];
            let trueFalseQuestion: TrueFalseQuestion[] = [];
            let fillInTheBlanksQuestion: FillInTheBlanksQuestion[] = [];
            let pictureQuestionQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response = await fetch(process.env.REACT_APP_API_URL + 'AnimalTrueFalse.json');
                trueFalseQuestion = await response.json();

                const response1 = await fetch(process.env.REACT_APP_API_URL + 'AnimalMultipleChoice.json');
                multipleChoiceQuestion = await response1.json();

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'AnimalIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = await response2.json();

                const response3 = await fetch(process.env.REACT_APP_API_URL + 'AnimalFillInTheBlanks.json');
                fillInTheBlanksQuestion = await response3.json();

                const response4 = await fetch(process.env.REACT_APP_API_URL + 'AnimalPictureQuestion.json');
                pictureQuestionQuestion = await response4.json();

            } else {
                const module = await import('../../../datas/AnimalTrueFalse.json');
                trueFalseQuestion = module.default;

                const module1 = await import('../../../datas/AnimalMultipleChoice.json');
                multipleChoiceQuestion = module1.default;

                const module2 = await import('../../../datas/AnimalIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = module2.default;

                const module3 = await import('../../../datas/AnimalFillInTheBlanks.json');
                fillInTheBlanksQuestion = module3.default;

                const module4 = await import('../../../datas/AnimalPictureQuestion.json');
                pictureQuestionQuestion = module4.default;
            }
            setQuestionBank({
                picture: pictureQuestionQuestion,
                identifyOddOneOut: identifyOddOneOutQuestion,
                multipleChoice: multipleChoiceQuestion,
                trueFalse: trueFalseQuestion,
                fillInTheBlanks: fillInTheBlanksQuestion,
            });

            getRandomQuestion();
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Animal Quiz for UKG Kids | General Awareness Worksheet"; // Set page title
    }, []);

    useEffect(() => {
        if (questionBank.identifyOddOneOut.length || questionBank.multipleChoice.length || questionBank.trueFalse.length || questionBank.fillInTheBlanks.length) {
            getRandomQuestion();
        }
    }, [questionBank]);

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Test your child's knowledge with this fun animal quiz for UKG students. Improve general awareness and build vocabulary with interactive animal-based questions!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/animals/quiz" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Animal Quiz</h1>

                                <p>
                                    Looking for a playful way to test your child’s knowledge of animals? This <strong>UKG-level animal quiz</strong> is perfect for young learners to improve their <strong>general awareness</strong> and reinforce what they’ve learned about animals through engaging, age-appropriate questions.
                                </p>

                                <h2>🎯 Learning Goals</h2>
                                <ul>
                                    <li>Identify animals based on features or sounds</li>
                                    <li>Strengthen listening, thinking, and comprehension skills</li>
                                    <li>Encourage participation through interactive learning</li>
                                </ul>

                                <h2>🧠 What’s in the Quiz?</h2>
                                <p>
                                    The quiz includes multiple-choice and true/false questions on commonly known animals such as <em>cat, dog, lion, elephant, cow, duck, and monkey</em>. Questions are designed to be simple and fun, ideal for 4–6-year-old children.
                                </p>


                                <h2>🧩 Sample Quiz Questions</h2>
                                <ul>
                                    <li><strong>Which animal barks?</strong><br />a) Cow &nbsp; b) Dog &nbsp; c) Cat</li>
                                    <li><strong>True or False:</strong> A lion lives in the jungle.</li>
                                    <li><strong>Which animal gives us milk?</strong><br />a) Goat &nbsp; b) Tiger &nbsp; c) Monkey</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>✨ Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Read out the questions to help children if needed</li>
                                    <li>Use animal toys or flashcards to support learning</li>
                                    <li>Celebrate correct answers with praise or small rewards</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Animal Quiz</h1>

                            <p>
                                Looking for a playful way to test your child’s knowledge of animals? This <strong>UKG-level animal quiz</strong> is perfect for young learners to improve their <strong>general awareness</strong> and reinforce what they’ve learned about animals through engaging, age-appropriate questions.
                            </p>

                            <h2>🎯 Learning Goals</h2>
                            <ul>
                                <li>Identify animals based on features or sounds</li>
                                <li>Strengthen listening, thinking, and comprehension skills</li>
                                <li>Encourage participation through interactive learning</li>
                            </ul>

                            <h2>🧠 What’s in the Quiz?</h2>
                            <p>
                                The quiz includes multiple-choice and true/false questions on commonly known animals such as <em>cat, dog, lion, elephant, cow, duck, and monkey</em>. Questions are designed to be simple and fun, ideal for 4–6-year-old children.
                            </p>


                            <h2>🧩 Sample Quiz Questions</h2>
                            <ul>
                                <li><strong>Which animal barks?</strong><br />a) Cow &nbsp; b) Dog &nbsp; c) Cat</li>
                                <li><strong>True or False:</strong> A lion lives in the jungle.</li>
                                <li><strong>Which animal gives us milk?</strong><br />a) Goat &nbsp; b) Tiger &nbsp; c) Monkey</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>✨ Tips for Parents and Teachers</h2>
                            <ul>
                                <li>Read out the questions to help children if needed</li>
                                <li>Use animal toys or flashcards to support learning</li>
                                <li>Celebrate correct answers with praise or small rewards</li>
                            </ul>
                        </article>
                        {currentQuestion ? (
                            <>
                                <h3 className="mb-2">{currentQuestion.question}</h3>

                                {"options" in currentQuestion ? (
                                    // Handle multiple-choice or identify odd one out
                                    typeof currentQuestion.options[0] === "string" ? (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            {(currentQuestion as IdentifyOddOneOutQuestion | MultipleChoiceQuestion).options.map((option, index) => (
                                                <IonItem key={index}
                                                    className={`grid-item ${selectedOption === option
                                                        ? isCorrect === true
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}>
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonRadioGroup>
                                    ) : (
                                        // Handle picture-based questions
                                        <div className="row">
                                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                                <div
                                                    className={`col-6 col-md-3 mb-5 `}
                                                    key={index}>
                                                    <img
                                                        src={process.env.REACT_APP_API_URL + option.src}
                                                        onClick={() => handleAnswerSubmit(option.src)}
                                                        className={`img-thumbnail ${selectedOption === option.src
                                                            ? isCorrect === true
                                                                ? "correct"
                                                                : "errorImgItems"
                                                            : ""
                                                            }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            height: '150px',
                                                            width: '150px'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) : (
                                    // Handle True/False questions
                                    <>
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                                            <IonItem
                                                className={`grid-item ${selectedOption === true
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={true} />
                                                <IonLabel>True</IonLabel>
                                            </IonItem>
                                            <IonItem
                                                className={`grid-item ${selectedOption === false
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={false} />
                                                <IonLabel>False</IonLabel>
                                            </IonItem>
                                        </IonRadioGroup>
                                    </>
                                )}
                            </>
                        ) : (
                            <p>Loading question...</p>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default AnimalQuiz;
